import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import TitleScreen from "./TitleScreen";
import AlreadyPlayedScreen from "./AlreadyPlayedScreen";
import HowToPlayModal from "./HowToPlayModal";
import MenuOverlay from "./MenuOverlay";
import Chronologicle from "./Chronologicle";
import GameOver from "./GameOver";
import SuperScoreRound from "./SuperScoreRound";
import Leaderboard from "./Components/Leaderboard";
import MyStatsPage from "./MyStatsPage";
import ProfileMenu from "./ProfileMenu";
import PostGameMenu from "./Components/PostGameMenu";
import DailyStatusScreen from "./DailyStatusScreen";

const noop = () => { };

const baseEvents = [
  { id: "rome", event: "Founding of Rome", year: -753 },
  { id: "magna-carta", event: "Signing of the Magna Carta", year: 1215 },
  { id: "printing-press", event: "Gutenberg perfects the movable type printing press", year: 1450 },
  { id: "moon-landing", event: "Apollo 11 lands on the Moon", year: 1969 },
  { id: "internet", event: "The modern public internet begins to take shape", year: 1983 },
  { id: "genome", event: "The Human Genome Project is declared complete", year: 2003 },
];

const guessBreakdown = [
  {
    event: "The longest event name in this responsive suite is deliberately here to stress truncation behavior without changing copy.",
    guess: 1912,
    year: 1914,
    points: 9,
  },
  { event: "Signing of the Magna Carta", guess: 1215, year: 1215, points: 10 },
  { event: "Apollo 11 lands on the Moon", guess: 1972, year: 1969, points: 8 },
  { event: "The modern public internet begins to take shape", guess: 1981, year: 1983, points: 9 },
  { event: "The Human Genome Project is declared complete", guess: 2000, year: 2003, points: 8 },
];

const leaderboardPlayers = [
  {
    rank: "1",
    name: "Alexandria",
    score: 94,
    userId: "user_alexandria",
    numGuesses: 1,
    events: guessBreakdown.map((entry) => ({
      event: entry.event,
      guess: entry.guess,
      actual: entry.year,
      points: entry.points,
    })),
  },
  {
    rank: "2",
    name: "Chronos",
    score: 81,
    userId: "user_chronos",
    numGuesses: 2,
    events: [
      { event: "Founding of Rome", guess: -700, actual: -753, points: 6 },
      { event: "Apollo 11 lands on the Moon", guess: 1967, actual: 1969, points: 9 },
    ],
  },
];

const historyItems = [
  {
    id: "2026-03-24",
    dateKey: "2026-03-24",
    mode: "daily",
    score: 94,
    numGuesses: 1,
    timestamp: Date.parse("2026-03-24T12:00:00Z"),
    events: guessBreakdown,
  },
  {
    id: "2026-03-23",
    dateKey: "2026-03-23",
    mode: "daily",
    score: 82,
    numGuesses: 2,
    timestamp: Date.parse("2026-03-23T12:00:00Z"),
    events: [
      { event: "Founding of Rome", guess: -740, actual: -753, points: 7 },
      { event: "Signing of the Magna Carta", guess: 1200, actual: 1215, points: 6 },
      { event: "Apollo 11 lands on the Moon", guess: 1968, actual: 1969, points: 9 },
    ],
  },
];

const findVisibleElementByText = (text) => {
  const normalizedTarget = text.replace(/\s+/g, " ").trim().toLowerCase();

  return Array.from(document.querySelectorAll("body *"))
    .filter((element) => {
      const style = window.getComputedStyle(element);
      if (style.display === "none" || style.visibility === "hidden") return false;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      const content = element.textContent?.replace(/\s+/g, " ").trim().toLowerCase();
      return content && content.includes(normalizedTarget);
    })
    .sort((left, right) => {
      const leftRect = left.getBoundingClientRect();
      const rightRect = right.getBoundingClientRect();
      return (leftRect.width * leftRect.height) - (rightRect.width * rightRect.height);
    })[0] || null;
};

const serializeRect = (rect) => ({
  top: Math.round(rect.top),
  right: Math.round(rect.right),
  bottom: Math.round(rect.bottom),
  left: Math.round(rect.left),
  width: Math.round(rect.width),
  height: Math.round(rect.height),
});

const measureElementWithinViewport = (element, label, viewport, tolerance) => {
  if (!element) {
    return {
      label,
      pass: false,
      reason: "not-found",
    };
  }

  const rect = element.getBoundingClientRect();
  const pass =
    rect.top >= -tolerance &&
    rect.left >= -tolerance &&
    rect.bottom <= viewport.height + tolerance &&
    rect.right <= viewport.width + tolerance;

  return {
    label,
    pass,
    rect: serializeRect(rect),
  };
};

const ensureResponsiveStorage = () => {
  if (typeof window === "undefined") return;

  localStorage.setItem("guestUid", "responsive_guest");
  localStorage.setItem("equippedEmblem", "13guest.png");
  localStorage.setItem("equippedTitle", "the Chronologicle");
};

const createScenarioDefinitions = () => ({
  daily_unavailable: {
    criticalTexts: ["Today’s Chronologicle isn’t available yet.", "Game #447", "Try Again"],
    render: () => (
      <DailyStatusScreen
        status="unavailable"
        currentDay={447}
        onRetry={noop}
      />
    ),
  },
  title_signed_out: {
    criticalTexts: ["Chronologicle", "Google Sign In", "Play as Guest"],
    render: () => (
      <TitleScreen
        onStart={noop}
        displayName=""
        setDisplayName={noop}
        currentDay={321}
        setActivePanel={noop}
        setPreviousPanel={noop}
        autoShowAlreadyPlayed={false}
        setAutoShowAlreadyPlayed={noop}
        setAuthChecked={noop}
        authChecked={true}
        testData={{ user: null }}
      />
    ),
  },
  title_ready: {
    criticalTexts: ["Hello, Alexandria", "Chronologicle", "Play Chronologicle"],
    render: () => (
      <TitleScreen
        onStart={noop}
        displayName="Alexandria"
        setDisplayName={noop}
        currentDay={321}
        setActivePanel={noop}
        setPreviousPanel={noop}
        autoShowAlreadyPlayed={false}
        setAutoShowAlreadyPlayed={noop}
        setAuthChecked={noop}
        authChecked={true}
        testData={{ user: { uid: "user_alex", displayName: "Alexandria" } }}
      />
    ),
  },
  title_name_input: {
    criticalTexts: ["Chronologicle", "Play Chronologicle"],
    criticalPlaceholders: ["Enter display name"],
    render: () => (
      <TitleScreen
        onStart={noop}
        displayName=""
        setDisplayName={noop}
        currentDay={321}
        setActivePanel={noop}
        setPreviousPanel={noop}
        autoShowAlreadyPlayed={false}
        setAutoShowAlreadyPlayed={noop}
        setAuthChecked={noop}
        authChecked={true}
        testData={{ showNameInput: true, tempName: "HistoryBuff" }}
      />
    ),
  },
  already_played: {
    criticalTexts: ["Until Next Daily", "Come Back and Play Again!", "My Stats", "Leaderboard"],
    render: () => (
      <AlreadyPlayedScreen
        onGoBack={noop}
        setActivePanel={noop}
        setAutoShowAlreadyPlayed={noop}
      />
    ),
  },
  how_to_play: {
    criticalTexts: ["How to Play Chronologicle", "Round 1:", "Round 2:", "0–29: Keep Trying"],
    render: () => <HowToPlayModal onClose={noop} />,
  },
  menu_overlay: {
    criticalTexts: ["My Stats", "Leaderboard", "Return to Game"],
    render: () => <MenuOverlay onClose={noop} onLeaderboard={noop} onStats={noop} />,
  },
  game_board: {
    criticalTexts: ["Chronologicle #321", "Submit"],
    render: () => (
      <Chronologicle
        hasStarted={true}
        displayName="Alexandria"
        onScoreUpdate={noop}
        equippedEmblem="13guest.png"
        equippedTitle="the Chronologicle"
        currentDay={321}
        setCurrentDay={noop}
        isPremiumMode={false}
        initialEvents={baseEvents}
        scoreFinalized={false}
        setScoreFinalized={noop}
        setActivePanel={noop}
        setShowLocker={noop}
        onPlayUnlimited={noop}
      />
    ),
  },
  game_over: {
    criticalTexts: ["Game Over", "You ran out of lives.", "RETRY", "SHOW ORDER"],
    render: () => <GameOver onRetry={noop} onShowCorrect={noop} />,
  },
  super_score_round: {
    criticalTexts: ["Drag the slider below to your best guess", "1000 BCE", "Guess"],
    render: () => (
      <SuperScoreRound
        playerOrder={baseEvents}
        chronoscore={40}
        onScoreFinalized={noop}
        onScoreUpdate={noop}
        isSuperScore={true}
        displayName="Alexandria"
        currentDay={321}
        isPremiumMode={false}
        setScoreFinalized={noop}
        setActivePanel={noop}
        setShowLocker={noop}
        onPlayUnlimited={noop}
        numGuesses={1}
        testData={{
          currentIndex: 0,
          guess: -1000,
          timelineStart: -1200,
          feedbackText: "Drag the slider below to your best guess",
        }}
      />
    ),
  },
  super_score_round_ce: {
    criticalTexts: ["Drag the slider below to your best guess", "1950", "Guess"],
    render: () => (
      <SuperScoreRound
        playerOrder={baseEvents}
        chronoscore={40}
        onScoreFinalized={noop}
        onScoreUpdate={noop}
        isSuperScore={true}
        displayName="Alexandria"
        currentDay={321}
        isPremiumMode={false}
        setScoreFinalized={noop}
        setActivePanel={noop}
        setShowLocker={noop}
        onPlayUnlimited={noop}
        numGuesses={1}
        testData={{
          currentIndex: 3,
          guess: 1950,
          timelineStart: 1000,
          feedbackText: "Drag the slider below to your best guess",
        }}
      />
    ),
  },
  super_score_final: {
    criticalTexts: ["Chronoscore:", "Share Score", "My Stats", "Leaderboard"],
    render: () => (
      <SuperScoreRound
        playerOrder={baseEvents}
        chronoscore={40}
        onScoreFinalized={noop}
        onScoreUpdate={noop}
        isSuperScore={true}
        displayName=""
        currentDay={321}
        isPremiumMode={false}
        setScoreFinalized={noop}
        setActivePanel={noop}
        setShowLocker={noop}
        onPlayUnlimited={noop}
        numGuesses={1}
        testData={{
          allGuesses: guessBreakdown,
          finalScore: 84,
          displayedScore: 84,
        }}
      />
    ),
  },
  share_image_modal: {
    criticalTexts: ["Long press or right click to save image.", "Close"],
    render: () => (
      <SuperScoreRound
        playerOrder={baseEvents}
        chronoscore={40}
        onScoreFinalized={noop}
        onScoreUpdate={noop}
        isSuperScore={true}
        displayName=""
        currentDay={321}
        isPremiumMode={false}
        setScoreFinalized={noop}
        setActivePanel={noop}
        setShowLocker={noop}
        onPlayUnlimited={noop}
        numGuesses={1}
        testData={{
          allGuesses: guessBreakdown,
          finalScore: 84,
          displayedScore: 84,
          shareImageUrl: "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=",
        }}
      />
    ),
  },
  post_game_menu: {
    criticalTexts: ["My Stats", "View Leaderboard"],
    render: () => (
      <PostGameMenu
        onClose={noop}
        onStats={noop}
        onLeaderboard={noop}
        onUnlimited={noop}
      />
    ),
  },
  leaderboard_empty: {
    criticalTexts: ["Leaderboard", "No scores submitted yet."],
    render: () => (
      <div style={{ backgroundColor: "#034c39", minHeight: "100dvh" }}>
        <Leaderboard
          onReturn={noop}
          setMenuClicked={noop}
          testData={{ players: [], loading: false, gameDay: 321 }}
        />
      </div>
    ),
  },
  leaderboard_error: {
    criticalTexts: ["Leaderboard", "We couldn’t load today’s leaderboard.", "Retry"],
    render: () => (
      <div style={{ backgroundColor: "#034c39", minHeight: "100dvh" }}>
        <Leaderboard
          onReturn={noop}
          setMenuClicked={noop}
          testData={{ players: [], loading: false, error: "We couldn’t load today’s leaderboard.", gameDay: 321 }}
        />
      </div>
    ),
  },
  leaderboard_populated: {
    criticalTexts: ["Leaderboard", "Alexandria", "94"],
    render: () => (
      <div style={{ backgroundColor: "#034c39", minHeight: "100dvh" }}>
        <Leaderboard
          onReturn={noop}
          currentUid="user_alexandria"
          setMenuClicked={noop}
          testData={{
            players: leaderboardPlayers,
            average: 88,
            loading: false,
            gameDay: 321,
            expandedPlayer: 0,
          }}
        />
      </div>
    ),
  },
  stats_guest: {
    criticalTexts: ["My Stats", "Log in with Google", "Return Home"],
    render: () => <MyStatsPage onReturn={noop} testData={{ user: null, loading: false }} />,
  },
  stats_user: {
    criticalTexts: ["Alexandria", "Recent Games", "High Score"],
    render: () => (
      <MyStatsPage
        onReturn={noop}
        testData={{
          user: { uid: "user_alex", displayName: "Alexandria" },
          loading: false,
          history: historyItems,
          expandedId: "2026-03-24",
          startDate: "2025-01-01T00:00:00.000Z",
        }}
      />
    ),
  },
  profile_name: {
    criticalTexts: ["Edit Name", "Cancel", "Save"],
    criticalPlaceholders: ["Enter name"],
    render: () => (
      <ProfileMenu
        displayName="Alexandria"
        setDisplayName={noop}
        onClose={noop}
        equippedEmblem="13guest.png"
        setEquippedEmblem={noop}
        equippedTitle="the Chronologicle"
        setEquippedTitle={noop}
        testData={{ activeSection: "name" }}
      />
    ),
  },
  profile_emblem: {
    criticalTexts: ["Edit Emblem", "Cancel", "Save"],
    render: () => (
      <ProfileMenu
        displayName="Alexandria"
        setDisplayName={noop}
        onClose={noop}
        equippedEmblem="13guest.png"
        setEquippedEmblem={noop}
        equippedTitle="the Chronologicle"
        setEquippedTitle={noop}
        testData={{ activeSection: "emblem" }}
      />
    ),
  },
  profile_title: {
    criticalTexts: ["Edit Title", "Cancel", "Save", "the Chronologicle"],
    render: () => (
      <ProfileMenu
        displayName="Alexandria"
        setDisplayName={noop}
        onClose={noop}
        equippedEmblem="13guest.png"
        setEquippedEmblem={noop}
        equippedTitle="the Chronologicle"
        setEquippedTitle={noop}
        testData={{ activeSection: "title" }}
      />
    ),
  },
});

const measureScenario = (scenarioName, definition) => {
  const tolerance = 2;
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };
  const checks = [];
  const documentRoot = document.scrollingElement || document.documentElement;
  const screenRoot = document.querySelector("[data-responsive-scenario-root] > *");

  checks.push({
    label: "scenario-root-present",
    pass: Boolean(screenRoot),
  });

  if (screenRoot) {
    checks.push(
      measureElementWithinViewport(
        screenRoot,
        "scenario-root-within-viewport",
        viewport,
        tolerance
      )
    );
  }

  checks.push({
    label: "no-document-horizontal-overflow",
    pass: documentRoot.scrollWidth <= viewport.width + tolerance,
    scrollWidth: documentRoot.scrollWidth,
    viewportWidth: viewport.width,
  });

  checks.push({
    label: "no-document-vertical-overflow",
    pass: documentRoot.scrollHeight <= viewport.height + tolerance,
    scrollHeight: documentRoot.scrollHeight,
    viewportHeight: viewport.height,
  });

  for (const text of definition.criticalTexts || []) {
    checks.push(
      measureElementWithinViewport(
        findVisibleElementByText(text),
        `critical-text:${text}`,
        viewport,
        tolerance
      )
    );
  }

  for (const placeholder of definition.criticalPlaceholders || []) {
    const element = document.querySelector(`[placeholder="${placeholder}"]`);
    checks.push(
      measureElementWithinViewport(
        element,
        `critical-placeholder:${placeholder}`,
        viewport,
        tolerance
      )
    );
  }

  const failedChecks = checks.filter((check) => !check.pass);

  return {
    scenario: scenarioName,
    viewport,
    pass: failedChecks.length === 0,
    failedChecks,
    checks,
  };
};

function ResponsiveScenarioFrame({ scenarioName }) {
  ensureResponsiveStorage();

  const scenarios = useMemo(() => createScenarioDefinitions(), []);
  const scenario = scenarios[scenarioName];

  const [result, setResult] = useState(() => ({
    scenario: scenarioName,
    pass: false,
    pending: true,
  }));

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  }, []);

  useEffect(() => {
    if (!scenario) {
      setResult({
        scenario: scenarioName,
        pass: false,
        error: `Unknown responsive scenario: ${scenarioName}`,
      });
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setResult(measureScenario(scenarioName, scenario));
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [scenario, scenarioName]);

  if (!scenario) {
    return (
      <>
        <div>Unknown responsive scenario: {scenarioName}</div>
        <div
          id="responsive-test-result"
          data-json={encodeURIComponent(JSON.stringify(result))}
          style={{ display: "none" }}
        />
      </>
    );
  }

  return (
    <>
      <div data-responsive-scenario-root>{scenario.render()}</div>
      <div
        id="responsive-test-result"
        data-json={encodeURIComponent(JSON.stringify(result))}
        style={{ display: "none" }}
      />
    </>
  );
}

function ResponsiveScenarioHarness({ scenarioName, frameWidth, frameHeight }) {
  const iframeRef = useRef(null);
  const [result, setResult] = useState(() => ({
    scenario: scenarioName,
    pass: false,
    pending: true,
  }));

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let intervalId = null;

    const readFrameResult = () => {
      const resultNode = iframe.contentDocument?.getElementById("responsive-test-result");
      const encoded = resultNode?.dataset?.json;
      if (!encoded) return;

      const parsed = JSON.parse(decodeURIComponent(encoded));
      setResult(parsed);

      if (!parsed.pending && intervalId) {
        window.clearInterval(intervalId);
      }
    };

    const handleLoad = () => {
      readFrameResult();
      intervalId = window.setInterval(readFrameResult, 100);
    };

    iframe.addEventListener("load", handleLoad);

    return () => {
      iframe.removeEventListener("load", handleLoad);
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [frameHeight, frameWidth, scenarioName]);

  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams();
    params.set("responsiveScenario", scenarioName);
    params.set("responsiveFrame", "1");
    return `${window.location.pathname}?${params.toString()}`;
  }, [scenarioName]);

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#111",
        }}
      >
        <iframe
          ref={iframeRef}
          title={`responsive-${scenarioName}`}
          src={iframeSrc}
          style={{
            width: `${frameWidth}px`,
            height: `${frameHeight}px`,
            border: "0",
            overflow: "hidden",
            background: "#034c39",
          }}
        />
      </div>
      <div
        id="responsive-test-result"
        data-json={encodeURIComponent(JSON.stringify(result))}
        style={{ display: "none" }}
      />
    </>
  );
}

export default function ResponsiveTestApp() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const scenarioName = params.get("responsiveScenario") || "title_signed_out";
  const isFrame = params.get("responsiveFrame") === "1";
  const frameWidth = Number(params.get("frameWidth") || 390);
  const frameHeight = Number(params.get("frameHeight") || 844);

  if (isFrame) {
    return <ResponsiveScenarioFrame scenarioName={scenarioName} />;
  }

  return (
    <ResponsiveScenarioHarness
      scenarioName={scenarioName}
      frameWidth={frameWidth}
      frameHeight={frameHeight}
    />
  );
}
